/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';

import { Grid, IconButton, Typography, makeStyles } from '@material-ui/core';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import TodayIcon from '@material-ui/icons/Today';
import CloseIcon from '@material-ui/icons/Close';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import {
  PeriodCountAccess,
  DateFilterFields,
} from 'modules/environments/interfaces';
import { isClientOperator } from 'utils/role';
import { RolesEnum } from 'modules/authenticate/interfaces';
import { filterPeriodOptions } from 'utils/graphs';
import FilterSelector from 'components/FilterSelector';
import { setPeriodFilter } from 'modules/environments/actions';
import i18n from 'i18n';
import { Formik } from 'formik';
import Button from 'components/Button';
import DatePicker from 'components/DatePicker';
import { formatDateForDateFilter } from 'utils/tsHelpers';
import SessionHistoryGraph from './SessionHistoryGraph';
import styles, { overrideFilter } from './styles';

interface SessionHistoryModalProps {
  dates: DateFilterFields;
  periodFilter: PeriodCountAccess;
  role?: RolesEnum;
  setDates: React.Dispatch<React.SetStateAction<DateFilterFields>>;
  setPeriodFilter: (prop: string) => void;
}

const SessionHistoryModal = ({
  dates,
  periodFilter,
  role,
  setDates,
  setPeriodFilter,
}: SessionHistoryModalProps) => {
  const classes = makeStyles(styles())();

  const [dateFilter, setDateFilter] = React.useState(true);

  const handleSubmit = (values: DateFilterFields) => {
    setDates({
      'start-date': formatDateForDateFilter(values?.['start-date']),
      'end-date': formatDateForDateFilter(values?.['end-date']),
    });
  };

  return (
    <Grid container className={classes.modalGraph}>
      <Grid container xs={8} className={classes.filterDate}>
        <Grid item className={classes.filterSelector}>
          {dateFilter ? (
            <Button
              startIcon={<TodayIcon />}
              variant="outlined"
              onClick={() => setDateFilter(false)}
              className={classes.applyButton}
            >
              {i18n.t('APPLY_DATE_FILTERS_KEY')}
            </Button>
          ) : (
            <>
              <Formik initialValues={dates} onSubmit={handleSubmit}>
                {({ setFieldValue, submitForm, values }) => (
                  <Grid spacing={2} container justify="flex-start">
                    <IconButton
                      onClick={() => {
                        setDateFilter(true);
                        setDates({ 'start-date': '', 'end-date': '' });
                        setFieldValue('start-date', null);
                        setFieldValue('end-date', null);
                      }}
                    >
                      <CloseIcon className={classes.closeIcon} />
                    </IconButton>
                    <DatePicker
                      id="start_date"
                      type="datetimeFilter"
                      label={i18n.t('START_TIME_KEY')}
                      keyValue="start-date"
                      value={values?.['start-date']}
                      setFieldsValue={setFieldValue}
                      shrink={
                        values?.['start-date'] !== '' &&
                        values?.['start-date'] !== null &&
                        values?.['start-date'] !== undefined
                      }
                    />
                    <DatePicker
                      id="end_date"
                      type="datetimeFilter"
                      label={i18n.t('END_TIME_KEY')}
                      keyValue="end-date"
                      value={values?.['end-date']}
                      minDate={values?.['start-date']}
                      setFieldsValue={setFieldValue}
                      shrink={
                        values?.['end-date'] !== '' &&
                        values?.['end-date'] !== null &&
                        values?.['end-date'] !== undefined
                      }
                    />
                    <Grid item>
                      <Button
                        id="submit-button"
                        onClick={submitForm}
                        submitButton
                        disabled={
                          !values?.['start-date'] ||
                          !values?.['end-date'] ||
                          values?.['start-date'] > values?.['end-date']
                        }
                      >
                        {i18n.t('APPLY_KEY')}
                      </Button>
                      {(values?.['start-date'] || values?.['end-date']) && (
                        <Button
                          id="clear-button"
                          onClick={() => {
                            setFieldValue('start-date', null);
                            setFieldValue('end-date', null);
                          }}
                          deleteButton
                        >
                          {i18n.t('CLEAR_FORM_KEY')}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Formik>
            </>
          )}
        </Grid>
      </Grid>
      <Grid container xs={4} className={classes.legend}>
        <Grid item className={classes.filterSelectorPeriod}>
          {dateFilter && (
            <FilterSelector
              overrideClasses={overrideFilter}
              options={filterPeriodOptions(setPeriodFilter)}
              currentValue={{
                label: i18n.t(
                  `ENVIRONMENT_DASHBOARD.LAST_${periodFilter.toUpperCase()}`
                ),
                onClick: () => null,
              }}
            />
          )}
        </Grid>
        <Grid item className={classes.labelsModal}>
          <FiberManualRecordIcon className={classes.graphSessionsMax} />
          <Typography className={classes.graphLegendItem}>
            {i18n.t('ENVIRONMENT_DASHBOARD.SESSIONS_MAX_KEY')}
          </Typography>
        </Grid>
        {!isClientOperator(role) && (
          <Grid item className={classes.labelsModal}>
            <FiberManualRecordIcon className={classes.graphVacanciesMax} />
            <Typography className={classes.graphLegendItem}>
              {i18n.t('ENVIRONMENT_DASHBOARD.VACANCIES_MAX_KEY')}
            </Typography>
          </Grid>
        )}
      </Grid>
      <SessionHistoryGraph isModal />
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { periodFilter },
}: ApplicationState) => ({
  role: data?.role,
  periodFilter,
});

export default connect(mapStateToProps, {
  setPeriodFilter,
})(SessionHistoryModal);
